import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { IconX } from '@tabler/icons-react'

export default function Glazes({ theme: t }) {
  const [glazes, setGlazes] = useState([])
  const [favorites, setFavorites] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('glazes')
  const [showColorPicker, setShowColorPicker] = useState(null)
  const [colorInput, setColorInput] = useState('')

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    const [glazesRes, favesRes] = await Promise.all([
      supabase.from('glazes').select('*').order('name'),
      supabase.from('glaze_favorites').select('*').order('created_at', { ascending: false }),
    ])
    if (glazesRes.data) setGlazes(glazesRes.data)
    if (favesRes.data) setFavorites(favesRes.data)
    setLoading(false)
  }

  const updateGlazeColor = async (glazeId, hex) => {
    await supabase.from('glazes').update({ color_hex: hex }).eq('id', glazeId)
    setGlazes(prev => prev.map(g => g.id === glazeId ? { ...g, color_hex: hex } : g))
    setShowColorPicker(null)
    setColorInput('')
  }

  const deleteFavorite = async (id) => {
    await supabase.from('glaze_favorites').delete().eq('id', id)
    setFavorites(prev => prev.filter(f => f.id !== id))
  }

  const PRESET_COLORS = [
    '#2a1a0a', '#5a3a20', '#8a6a40', '#c8a870',
    '#f0d890', '#e8c060', '#d4843a', '#c85020',
    '#8c2010', '#3a0808', '#f0a8a0', '#e87878',
    '#c84848', '#4a1818', '#7abfb8', '#3a8a84',
    '#1e5058', '#0a2830', '#4a7a8a', '#2a4a5a',
    '#6a8a5a', '#3a5a30', '#8ab870', '#c8d890',
    '#e8f0d0', '#f0f0e0', '#e0d8c8', '#c8bfa0',
    '#a89878', '#786848',
  ]

  if (loading) return (
    <div style={{ padding: '40px', textAlign: 'center', color: t.textLight, fontFamily: 'Jost, sans-serif', fontSize: '13px' }}>
      Loading...
    </div>
  )

  return (
    <div style={{ fontFamily: 'Jost, sans-serif', background: t.bg, minHeight: '100vh', maxWidth: '430px', margin: '0 auto', paddingBottom: '80px' }}>

      <div style={{ background: t.header, padding: '48px 16px 14px' }}>
        <div style={{ fontFamily: 'Dancing Script, cursive', fontSize: '28px', fontWeight: '700', color: '#e8f8f6', lineHeight: 1, marginBottom: '2px' }}>Glazes</div>
        <div style={{ fontSize: '10px', fontWeight: '300', color: t.textMuted, marginBottom: '14px' }}>
          {glazes.length} glazes · {favorites.length} saved combos
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {['glazes', 'favorites'].map(tab => (
            <div
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{ flex: 1, padding: '8px', borderRadius: '12px', fontSize: '12px', textAlign: 'center', cursor: 'pointer', background: activeTab === tab ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.07)', color: activeTab === tab ? '#e8f8f6' : t.textMuted }}
            >
              {tab === 'glazes' ? 'All Glazes' : 'Favorites'}
            </div>
          ))}
        </div>
      </div>

      {activeTab === 'glazes' && (
        <div style={{ padding: '12px 14px 24px' }}>
          {glazes.length === 0 ? (
            <div style={{ padding: '60px 24px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Dancing Script, cursive', fontSize: '24px', color: t.header, marginBottom: '8px' }}>No glazes yet</div>
              <div style={{ fontSize: '13px', color: t.textLight }}>Glazes appear here as you add them to pieces</div>
            </div>
          ) : (
            glazes.map(glaze => (
              <div key={glaze.id} style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: '12px', padding: '12px 14px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  onClick={() => { setShowColorPicker(glaze.id); setColorInput(glaze.color_hex || '') }}
                  style={{ width: '28px', height: '28px', borderRadius: '50%', background: glaze.color_hex || t.border, border: `2px solid ${t.border}`, flexShrink: 0, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {!glaze.color_hex && <span style={{ fontSize: '14px', color: t.textLight }}>+</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '400', color: t.text }}>{glaze.name}</div>
                  <div style={{ fontSize: '10px', fontWeight: '300', color: t.textLight, marginTop: '1px' }}>
                    used {glaze.times_used || 0} time{glaze.times_used !== 1 ? 's' : ''}
                  </div>
                </div>

                {showColorPicker === glaze.id && (
                  <div
                    onClick={() => setShowColorPicker(null)}
                    style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(0,0,0,.4)' }}
                  >
                    <div
                      onClick={e => e.stopPropagation()}
                      style={{ background: t.card, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '430px', padding: '20px 16px 40px' }}
                    >
                      <div style={{ fontFamily: 'Dancing Script, cursive', fontSize: '22px', color: t.header, marginBottom: '14px' }}>
                        Pick a color for {glaze.name}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px', marginBottom: '16px' }}>
                        {PRESET_COLORS.map(color => (
                          <div
                            key={color}
                            onClick={() => updateGlazeColor(glaze.id, color)}
                            style={{
                              width: '100%',
                              paddingBottom: '100%',
                              borderRadius: '50%',
                              background: color,
                              cursor: 'pointer',
                              border: glaze.color_hex === color ? `3px solid ${t.text}` : '2px solid transparent',
                              position: 'relative'
                            }}
                          />
                        ))}
                      </div>
                      <div style={{ fontSize: '9px', fontWeight: '500', letterSpacing: '.8px', textTransform: 'uppercase', color: t.textLight, marginBottom: '6px' }}>
                        Or enter hex code
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          value={colorInput}
                          onChange={e => setColorInput(e.target.value)}
                          placeholder="#000000"
                          style={{ flex: 1, background: t.bg, border: `1px solid ${t.border}`, borderRadius: '10px', padding: '9px 12px', fontSize: '13px', color: t.text, outline: 'none', fontFamily: 'Jost, sans-serif' }}
                        />
                        <div
                          onClick={() => colorInput && updateGlazeColor(glaze.id, colorInput)}
                          style={{ padding: '9px 16px', borderRadius: '10px', background: t.header, color: '#e8f8f6', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}
                        >
                          Apply
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'favorites' && (
        <div style={{ padding: '12px 14px 24px' }}>
          {favorites.length === 0 ? (
            <div style={{ padding: '60px 24px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Dancing Script, cursive', fontSize: '24px', color: t.header, marginBottom: '8px' }}>No favorites yet</div>
              <div style={{ fontSize: '13px', color: t.textLight }}>Save glaze combos from a piece card to see them here</div>
            </div>
          ) : (
            favorites.map(fave => (
              <div key={fave.id} style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: '14px', overflow: 'hidden', marginBottom: '10px' }}>
                {fave.preview_photo_url && (
                  <img src={fave.preview_photo_url} alt="" style={{ width: '100%', height: '120px', objectFit: 'cover' }}/>
                )}
                <div style={{ padding: '10px 12px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <div style={{ fontFamily: 'Alike, serif', fontSize: '13px', color: t.text }}>{fave.name}</div>
                    <div onClick={() => deleteFavorite(fave.id)} style={{ color: t.textLight, cursor: 'pointer' }}>
                      <IconX size={14}/>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {(fave.exterior_layers || []).filter(Boolean).map((layer, i) => (
                      <div key={i} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '8px', background: t.bg, border: `1px solid ${t.border}`, color: t.text }}>
                        {layer}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}